import type { CandidateProgress, WorkflowStageKind } from './onboarding';
import {
  type FormTemplateKind,
  STAGE_EMAIL_EVENTS,
  type StageEmailEvent,
  type WorkflowSubject,
} from './template';

/** What HR calls a step's type. `wait` is reserved. */
export const BLOCK_KIND_NAMES = [
  'registration',
  'form',
  'documents',
  'review',
  'hr_task',
  'signature',
  'notify',
] as const;
export type BlockKind = (typeof BLOCK_KIND_NAMES)[number];

/** Phase 1: exactly the events today's engine raises. */
export type MessageEvent = StageEmailEvent;
export const MESSAGE_EVENTS: readonly MessageEvent[] = STAGE_EMAIL_EVENTS;

export type ContextNamespace = 'candidate' | 'hr' | 'system' | 'event' | 'employee';

export type ValueType =
  | { type: 'text' }
  | { type: 'number' }
  | { type: 'boolean' }
  | { type: 'date' }
  | { type: 'datetime' }
  | { type: 'enum'; options: readonly string[] }
  | { type: 'file' };

/** `sensitive` is shown to HR as "Private answer". */
export interface ExportDecl {
  name: string;
  label: string;
  valueType: ValueType;
  sensitive: boolean;
}

export interface BlockKindSpec {
  kind: BlockKind;
  /** What `workflow_stages.kind` stores. */
  dbKind: WorkflowStageKind;
  label: string;
  sentence: string;
  completedBy: 'candidate' | 'hr' | 'provider' | 'system';
  takesForm: { kind: FormTemplateKind; required: boolean } | null;
  decisions: { approve: boolean; sendBack: boolean };
  /** An HR check checks the step directly before it; it may follow a registration, form or documents step. */
  checksPrevious?: { allowed: readonly BlockKind[] };
  events: readonly MessageEvent[];
  fixedExports: readonly ExportDecl[];
  progressWhenActive: CandidateProgress | null;
}

const text = (name: string, label: string): ExportDecl => ({
  name,
  label,
  valueType: { type: 'text' },
  sensitive: false,
});

export const BLOCK_KINDS: Record<BlockKind, BlockKindSpec> = {
  registration: {
    kind: 'registration',
    dbKind: 'self_registration',
    label: 'Registration',
    sentence: 'The candidate answers a questions form',
    completedBy: 'candidate',
    takesForm: { kind: 'form', required: true },
    decisions: { approve: false, sendBack: false },
    events: ['on_stage_activate'],
    fixedExports: [],
    progressWhenActive: 'pending_registration',
  },
  form: {
    kind: 'form',
    dbKind: 'form_submission',
    label: 'Candidate form',
    sentence: 'The candidate answers a questions form',
    completedBy: 'candidate',
    takesForm: { kind: 'form', required: true },
    decisions: { approve: false, sendBack: false },
    events: ['on_stage_activate'],
    fixedExports: [],
    progressWhenActive: 'pending_forms',
  },
  documents: {
    kind: 'documents',
    dbKind: 'document_upload',
    label: 'Document checklist',
    sentence: 'The candidate uploads the documents on a checklist',
    completedBy: 'candidate',
    takesForm: { kind: 'documents', required: true },
    decisions: { approve: false, sendBack: false },
    events: ['on_stage_activate'],
    fixedExports: [],
    progressWhenActive: 'pending_documents',
  },
  review: {
    kind: 'review',
    dbKind: 'hr_approval',
    label: 'HR check',
    sentence: 'HR checks what the candidate sent (can send it back)',
    completedBy: 'hr',
    takesForm: null,
    decisions: { approve: true, sendBack: true },
    checksPrevious: { allowed: ['registration', 'form', 'documents'] },
    events: ['on_stage_activate', 'on_hr_approve', 'on_hr_reject'],
    fixedExports: [
      {
        name: 'decision',
        label: 'Decision',
        valueType: { type: 'enum', options: ['approved'] },
        sensitive: false,
      },
      {
        name: 'decided_at',
        label: 'Decided at',
        valueType: { type: 'datetime' },
        sensitive: false,
      },
      text('notes', 'Notes'),
    ],
    progressWhenActive: 'under_review',
  },
  hr_task: {
    kind: 'hr_task',
    dbKind: 'hr_approval',
    label: 'HR to-do',
    sentence: 'HR does something and ticks it off',
    completedBy: 'hr',
    takesForm: { kind: 'form', required: false },
    decisions: { approve: true, sendBack: false },
    events: ['on_stage_activate', 'on_hr_approve'],
    fixedExports: [
      {
        name: 'completed_at',
        label: 'Completed at',
        valueType: { type: 'datetime' },
        sensitive: false,
      },
    ],
    progressWhenActive: 'pending_hr',
  },
  signature: {
    kind: 'signature',
    dbKind: 'signature_collection',
    label: 'Signature',
    sentence: 'The candidate and HR sign a document',
    completedBy: 'provider',
    takesForm: { kind: 'signature', required: true },
    decisions: { approve: false, sendBack: false },
    events: ['on_stage_activate'],
    fixedExports: [
      {
        name: 'status',
        label: 'Status',
        valueType: { type: 'enum', options: ['completed'] },
        sensitive: false,
      },
      { name: 'signed_at', label: 'Signed at', valueType: { type: 'datetime' }, sensitive: false },
    ],
    progressWhenActive: 'pending_signature',
  },
  notify: {
    kind: 'notify',
    dbKind: 'notification',
    label: 'Email',
    sentence: 'An email is sent and the workflow moves on',
    completedBy: 'system',
    takesForm: null,
    decisions: { approve: false, sendBack: false },
    events: ['on_stage_activate'],
    fixedExports: [],
    progressWhenActive: null,
  },
};

export interface TemplateTypeSpec {
  type: WorkflowSubject;
  label: string;
  selector: 'employeeType' | 'lifecycleKind';
  runSubject: 'candidate' | 'employee';
  hasPortal: boolean;
  contextNamespaces: readonly ContextNamespace[];
  allowedBlocks: readonly BlockKind[];
  messages: readonly MessageEvent[];
}

export const TEMPLATE_TYPES: Record<WorkflowSubject, TemplateTypeSpec> = {
  candidate_onboarding: {
    type: 'candidate_onboarding',
    label: 'Candidate onboarding',
    selector: 'employeeType',
    runSubject: 'candidate',
    hasPortal: true,
    contextNamespaces: ['candidate', 'hr', 'system', 'event'],
    allowedBlocks: [
      'registration',
      'form',
      'documents',
      'review',
      'hr_task',
      'signature',
      'notify',
    ],
    messages: STAGE_EMAIL_EVENTS,
  },
  employee_lifecycle: {
    type: 'employee_lifecycle',
    label: 'Employee workflow',
    selector: 'lifecycleKind',
    runSubject: 'employee',
    hasPortal: false,
    contextNamespaces: ['employee', 'hr', 'system'],
    allowedBlocks: ['hr_task'],
    messages: STAGE_EMAIL_EVENTS,
  },
};

/** A reference name: how a step is told apart from the others in one workflow. */
export const STEP_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

/** A reference name that would read as a source of values in a later phase. */
export const RESERVED_STEP_NAMESPACES: readonly string[] = [
  'candidate',
  'hr',
  'system',
  'event',
  'employee',
  'recipient',
  'nda',
  'defaults',
  'prefill',
];

export type CompileProblemCode =
  | 'NAME_INVALID'
  | 'NO_STEPS'
  | 'TOO_MANY_STEPS'
  | 'ORDER_DUPLICATE'
  | 'STEP_NAME_INVALID'
  | 'STEP_KIND_INVALID'
  | 'STEP_ORDER_INVALID'
  | 'STEP_KEY_INVALID'
  | 'STEP_KEY_DUPLICATE'
  | 'HR_APPROVAL_FLAG'
  | 'BLOCK_NOT_ALLOWED'
  | 'FORM_MISSING'
  | 'FORM_NOT_FOUND'
  | 'FORM_WRONG_KIND'
  | 'FORM_NO_KIND'
  | 'FORM_INACTIVE'
  | 'FORM_NOT_TAKEN'
  | 'FORM_NOT_ALLOWED'
  | 'CHECKS_PREVIOUS_MISPLACED'
  | 'CHECK_NOT_AFTER_SUBMISSION'
  | 'EMAIL_TEMPLATE_NOT_FOUND'
  | 'EMAIL_TEMPLATE_INACTIVE'
  | 'EMAIL_EVENT_NOT_ALLOWED'
  | 'EMAIL_VARIABLE_REFUSED'
  | 'EMAIL_ADDRESS_INVALID'
  | 'EMAIL_DUPLICATE'
  | 'NOTIFY_NEEDS_EMAIL'
  | 'SIGNATURE_FIRST'
  | 'SIGNATURE_REACH';

export interface CompileProblem {
  /** "Must fix before publishing" / "Worth checking". */
  severity: 'error' | 'warning';
  code: CompileProblemCode;
  /** Null: the workflow itself. */
  stageKey: string | null;
  stageOrder: number | null;
  slot: string | null;
  tab: 'setup' | 'messages' | 'skip' | 'information' | null;
  /** The HR voice. */
  message: string;
  /** Today's wording, as `{ where, message }` for the routes that existed before drafts. */
  legacyWhere: string;
  legacyMessage: string;
}

export interface CompiledMessage {
  event: MessageEvent;
  templateKey: string;
  to: 'candidate' | 'hr';
  cc: string[];
  bcc: string[];
}

export interface CompiledStage {
  key: string;
  name: string;
  order: number;
  block: BlockKind;
  dbKind: WorkflowStageKind;
  formTemplateId: string | null;
  /** Informational: the form's current version when published. The engine never reads it. */
  formVersionAtPublish: string | null;
  exports: ExportDecl[];
  /** The key of the registration, form or documents step directly before, for an HR check. */
  reviewsStageKey: string | null;
  messages: CompiledMessage[];
  instructions: string | null;
}

/** schemaVersion 1 is phase 1; a field a later phase adds bumps it. */
export interface CompiledWorkflow {
  schemaVersion: 1;
  type: WorkflowSubject;
  stages: CompiledStage[];
}
